import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionCard } from '../common';
import { FormFieldsSkeleton } from '../section-skeletons';

/**
 * Loading placeholders for `PartnerIntegrationSection`'s card. Three shapes,
 * because the section knows which card is coming before the inner read lands:
 * neutral (org read in flight), request form (no grant), callback (granted).
 * Never used on error — the section renders nothing there.
 */

/** The chrome all three share: eyebrow + chip header, hairline, body padding. */
function PartnerSkeletonCard({ children }: { children: ReactNode }) {
  return (
    <SectionCard tight>
      <div
        className="flex flex-wrap items-center justify-between gap-3"
        style={{ padding: '12px 18px 14px' }}
        aria-hidden="true"
      >
        {/* Eyebrow ("Partner integration" / "Partner callback") + status chip. */}
        <Skeleton className="h-4 w-40 rounded-md" />
        <Skeleton className="h-5 w-24 rounded-full" />
      </div>
      <div
        className="flex flex-col gap-3 border-t border-border"
        style={{ padding: '14px 18px 18px' }}
        aria-hidden="true"
      >
        {children}
      </div>
    </SectionCard>
  );
}

/**
 * Neutral: the role or org read is still in flight, so the grant is unknown.
 * Also what the route pending state renders, having no data at all yet.
 */
export function PartnerIntegrationCardSkeleton() {
  return (
    <PartnerSkeletonCard>
      <Skeleton data-testid="partner-neutral-block" className="h-4 w-3/4 rounded-md" />
      <Skeleton data-testid="partner-neutral-block" className="h-14 w-full rounded-md" />
      <Skeleton data-testid="partner-neutral-block" className="h-14 w-full rounded-md" />
      <Skeleton data-testid="partner-neutral-block" className="h-9 w-36 rounded-md" />
    </PartnerSkeletonCard>
  );
}

/**
 * No grant, access-request read in flight → `PartnerAccessRequestForm`: intro
 * copy, email, phone row, two dropdowns, reason, submit. Mirrors the form
 * rather than the shorter "already submitted" note a repeat visitor gets.
 */
export function PartnerAccessRequestCardSkeleton() {
  return (
    <PartnerSkeletonCard>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3 w-full rounded" />
        <Skeleton className="h-3 w-full rounded" />
        <Skeleton className="h-3 w-2/3 rounded" />
      </div>
      {/* Email. */}
      <FormFieldsSkeleton fields={1} />
      {/* Phone: one label over a country picker + the number input. */}
      <div data-testid="partner-phone-field-skeleton" className="flex flex-col gap-1.5">
        <Skeleton className="h-3 w-[90px] rounded" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-[110px] shrink-0 rounded-md" />
          <Skeleton className="h-9 flex-1 rounded-md" />
        </div>
      </div>
      {/* Inbox volume + use case. */}
      <FormFieldsSkeleton fields={2} />
      {/* Reason: the same label, but a 4-row textarea under it. */}
      <div data-testid="partner-reason-field-skeleton" className="flex flex-col gap-1.5">
        <Skeleton className="h-3 w-[90px] rounded" />
        <Skeleton className="h-[88px] w-full rounded-md" />
        <Skeleton className="h-3 w-3/4 rounded" />
      </div>
      <Skeleton className="h-8 w-[130px] rounded-md" />
    </PartnerSkeletonCard>
  );
}

/**
 * Granted, integration read in flight → the callback card. That read resolves
 * to either the summary or the register form, so this draws what both agree
 * on: two stacked entries over a trailing action row.
 */
export function PartnerCallbackCardSkeleton() {
  return (
    <PartnerSkeletonCard>
      <div className="flex flex-col gap-2.5 rounded-md border border-border p-3">
        {[0, 1].map((i) => (
          <div key={i} data-testid="partner-callback-entry-skeleton" className="flex items-start gap-2.5">
            <Skeleton className="mt-[3px] h-4 w-4 shrink-0 rounded" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Skeleton className="h-3.5 w-[220px] max-w-full rounded" />
              <Skeleton className="h-3 w-[280px] max-w-full rounded" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-8 w-[96px] rounded-md" />
        <Skeleton className="h-8 w-[72px] rounded-md" />
        <Skeleton className="ml-auto h-8 w-[80px] rounded-md" />
      </div>
    </PartnerSkeletonCard>
  );
}
